import { ReactNode, useEffect, useState, useCallback } from 'react';
import { AppContext, MenuLink } from './AppContext';
import { BreadcrumbLink } from '@/types/app';
import { RiDossierLine, RiLogoutBoxLine, RiUserLine } from '@remixicon/react';
import { ToastProvider } from '@/components/Toast';

interface AppProviderProps {
  children: ReactNode;
}

const defaultMenuLinks: MenuLink[] = [
  {
    id: 'patients',
    label: 'Pacientes',
    icon: <RiUserLine size={18} />,
    href: '/app',
  },
  {
    id: 'appointments',
    label: 'Atendimentos',
    icon: <RiDossierLine size={18} />,
    href: '/app/atendimentos',
  },
  {
    id: 'logout',
    label: 'Sair',
    icon: <RiLogoutBoxLine size={18} />,
    href: '/app/logout',
  },
];

export function AppProvider({ children }: AppProviderProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [menuLinks, setMenuLinks] = useState<MenuLink[]>(defaultMenuLinks);
  const [breadcrumbLinks, setBreadcrumbLinks] = useState<BreadcrumbLink[]>([]);

  const pathname =
    typeof window !== 'undefined' ? window.location.pathname : '';

  const getMenuLinksWithActive = useCallback(() => {
    const isPatientsPath =
      pathname === '/app' ||
      pathname === '/app/pacientes' ||
      pathname.startsWith('/app/pacientes/');

    return defaultMenuLinks.map((link) => {
      const isRoot = link.href === '/app';
      const isActive = isRoot
        ? isPatientsPath
        : pathname === link.href || pathname.startsWith(link.href + '/');
      return {
        ...link,
        isActive,
      };
    });
  }, [pathname]);

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const openSidebar = () => {
    setIsSidebarOpen(true);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  useEffect(() => {
    setMenuLinks(getMenuLinksWithActive());
  }, [getMenuLinksWithActive]);

  return (
    <AppContext.Provider
      value={{
        isSidebarOpen,
        toggleSidebar,
        openSidebar,
        closeSidebar,
        menuLinks,
        breadcrumbLinks,
        setBreadcrumbLinks,
        getMenuLinksWithActive,
      }}
    >
      <ToastProvider>
        {children}
      </ToastProvider>
    </AppContext.Provider>
  );
}
